/**
 * Custom List Node for Lexical
 * Extends ListNode with blockId support for AST compatibility
 */

import { ListNode, ListType, SerializedListNode } from '@lexical/list'
import { $applyNodeReplacement, DOMConversionMap, DOMExportOutput, EditorConfig, LexicalNode, Spread } from 'lexical'

export type SerializedCustomListNode = Spread<
  {
    blockId: string
    dir?: 'ltr' | 'rtl'
    ordered: boolean
  },
  SerializedListNode
>

export class CustomListNode extends ListNode {
  __blockId: string
  __ordered: boolean

  static getType(): string {
    return 'custom-list'
  }

  static clone(node: CustomListNode): CustomListNode {
    const cloned = new CustomListNode(node.__blockId, node.__ordered, node.__start, node.__key)
    cloned.__dir = node.__dir
    return cloned
  }

  constructor(blockId: string, ordered: boolean, start?: number, key?: string) {
    const listType: ListType = ordered ? 'number' : 'bullet'
    super(listType, start || 1, key)
    this.__blockId = blockId
    this.__ordered = ordered
  }

  getBlockId(): string {
    return this.__blockId
  }

  setBlockId(blockId: string): this {
    const writable = this.getWritable()
    writable.__blockId = blockId
    return writable
  }

  getDir(): 'ltr' | 'rtl' | null {
    return this.__dir
  }

  setDir(dir: 'ltr' | 'rtl' | null): this {
    const writable = this.getWritable()
    writable.__dir = dir
    // Keep Lexical's standard direction in sync — see CustomParagraphNode.setDir.
    writable.setDirection(dir)
    return writable
  }

  isOrdered(): boolean {
    return this.__ordered
  }

  setOrdered(ordered: boolean): this {
    const writable = this.getWritable()
    writable.__ordered = ordered
    return writable
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    dom.setAttribute('data-block-id', this.__blockId)
    if (this.__dir) {
      dom.setAttribute('dir', this.__dir)
      dom.style.direction = this.__dir
    }
    return dom
  }

  updateDOM(prevNode: ListNode, dom: HTMLElement, config: EditorConfig): boolean {
    const updated = super.updateDOM(prevNode, dom, config)
    const prevCustom = prevNode as CustomListNode
    if (prevCustom.__blockId !== this.__blockId) {
      dom.setAttribute('data-block-id', this.__blockId)
    }
    if (prevCustom.__dir !== this.__dir) {
      if (this.__dir) {
        dom.setAttribute('dir', this.__dir)
        dom.style.direction = this.__dir
      } else {
        dom.removeAttribute('dir')
        dom.style.direction = ''
      }
    }
    return updated || prevCustom.__ordered !== this.__ordered
  }

  static importDOM(): DOMConversionMap | null {
    return {
      ol: () => ({
        conversion: (element: HTMLElement) => {
          const blockId = element.getAttribute('data-block-id') || ''
          const dir = element.getAttribute('dir') as 'ltr' | 'rtl' | null
          const node = $createCustomListNode(blockId, true)
          if (dir) {
            node.setDir(dir)
          }
          return { node }
        },
        priority: 1,
      }),
      ul: () => ({
        conversion: (element: HTMLElement) => {
          const blockId = element.getAttribute('data-block-id') || ''
          const dir = element.getAttribute('dir') as 'ltr' | 'rtl' | null
          const node = $createCustomListNode(blockId, false)
          if (dir) {
            node.setDir(dir)
          }
          return { node }
        },
        priority: 1,
      }),
    }
  }

  exportDOM(): DOMExportOutput {
    const tag = this.__ordered ? 'ol' : 'ul'
    const element = document.createElement(tag)
    element.setAttribute('data-block-id', this.__blockId)
    if (this.__dir) {
      element.setAttribute('dir', this.__dir)
    }
    return { element }
  }

  static importJSON(serializedNode: SerializedCustomListNode): CustomListNode {
    const node = $createCustomListNode(serializedNode.blockId, serializedNode.ordered, serializedNode.start)
    if (serializedNode.dir) {
      node.setDir(serializedNode.dir)
    }
    return node
  }

  exportJSON(): SerializedCustomListNode {
    return {
      ...super.exportJSON(),
      type: 'custom-list',
      blockId: this.__blockId,
      dir: this.__dir || undefined,
      ordered: this.__ordered,
    }
  }
}

export function $createCustomListNode(blockId: string, ordered: boolean, start?: number): CustomListNode {
  return $applyNodeReplacement(new CustomListNode(blockId, ordered, start))
}

export function $isCustomListNode(node: LexicalNode | null | undefined): node is CustomListNode {
  return node instanceof CustomListNode
}
